import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '@environments/environment';

import { MappingService } from './mapping.service';
import { ReportService } from './report.service';
import { UiService } from './ui.service';

import { DateTimeFormat } from '@classes/date-time-format';
import { Mapping } from '@classes/mapping/mapping';
import { DateTime } from 'luxon';

@Injectable({
	providedIn: 'root'
})
export class ProfessionalStatusStructureService {
	structuresChanged = new Subject<Mapping[]>();

	private readonly context = 'ProfessionalStatus';
	private baseUrl = environment.apiUrl + 'professional-status-structure';
	private structures: Mapping[];

	constructor(
		private http: HttpClient,
		private uiSrv: UiService,
		private mappingSrv: MappingService,
		private reportSrv: ReportService
	) {}

	getList(): Observable<Mapping[]> {
		return this.http.get<Mapping[]>(this.baseUrl).pipe(
			tap(structuresFromApi => {
				this.structures = structuresFromApi;
				this.structuresChanged.next([...this.structures]);
			})
		);
	}

	get(structureId: number): Observable<Mapping> {
		this.uiSrv.isLoadingSubject.next(true);
		this.mappingSrv.structureChanged.next(null);

		this.reportSrv.getReportsFromContext(this.context, [{ id: structureId }]).subscribe();

		return this.http.get<Mapping>(`${this.baseUrl}/${structureId}`).pipe(
			tap(structureFromApi => {
				this.uiSrv.isLoadingSubject.next(false);
				this.mappingSrv.structureChanged.next(structureFromApi);
			})
		);
	}

	post(structure: Mapping): Observable<Mapping> {
		this.uiSrv.isLoadingSubject.next(true);

		return this.http.post<Mapping>(`${this.baseUrl}`, structure).pipe(
			tap(createdStructure => {
				this.structures.push(createdStructure);
				this.structuresChanged.next([...this.structures]);
				this.uiSrv.isLoadingSubject.next(false);
				this.uiSrv.showSnackbar('mapping.base.saved.summary', true);
			})
		);
	}

	put(structure: Mapping): Observable<Mapping> {
		this.uiSrv.isLoadingSubject.next(true);

		return this.http.put<Mapping>(`${this.baseUrl}`, structure).pipe(
			tap(structureFromApi => {
				this.structures = this.structures.map(s =>
					s.id === structureFromApi.id ? structureFromApi : s
				);
				this.structuresChanged.next([...this.structures]);
				this.mappingSrv.structureChanged.next(structureFromApi);
				this.uiSrv.isLoadingSubject.next(false);
				this.uiSrv.showSnackbar('mapping.base.saved.summary', true);
			})
		);
	}

	downloadExcel(structureId: number): Observable<Blob> {
		return this.http
			.get(`${this.baseUrl}/${structureId}/download-excel`, {
				observe: 'response',
				responseType: 'blob'
			})
			.pipe(map(res => this.saveFile(res)));
	}

	downloadJson(structureId: number): Observable<Blob> {
		return this.http
			.get(`${this.baseUrl}/${structureId}/download-json`, {
				observe: 'response',
				responseType: 'blob'
			})
			.pipe(map(res => this.saveFile(res)));
	}

	uploadJson(file: File, structureId: number): Observable<Mapping> {
		this.uiSrv.isLoadingSubject.next(true);
		const formData = new FormData();
		formData.append('file', file, file.name);

		const httpParams = new HttpParams().append('structureId', structureId.toString());

		return this.http
			.post<Mapping>(`${this.baseUrl}/upload-json`, formData, { params: httpParams })
			.pipe(
				tap({
					next: result => {
						this.resetMapping(result.structure);
						this.mappingSrv.structureChanged.next(result);
						this.uiSrv.isLoadingSubject.next(false);
						this.uiSrv.showSnackbar('mapping.base.jsonImported.summary', true);
					}
				})
			);
	}

    private resetMapping(structure: any[]): void {
        structure.forEach(s => {
            if (s.children?.length){
                this.resetMapping(s.children)
            }
            s.id = null;
            s.mappingId = null;
        });
    }

	private saveFile(res: HttpResponse<Blob>): Blob {
		const fileName =
			res.headers.get('filename') === null
				? this.uiSrv.translate('mapping.professionalStatus.jsonFileName.fullLabel', {
						date: DateTime.now().toFormat(DateTimeFormat.fullYear)
					})
				: res.headers.get('filename');
		FileSaver.saveAs(res.body, fileName);
		return res.body;
	}
}
